import React, { useState } from 'react';
import { useHistory } from 'react-router';
import { Analytics } from 'global';
import { PetInterface } from 'types/pet';
import { Helmet } from 'react-helmet-async';
import { KibButtonNew } from '@chewy/kib-controls-react';
import { KibHeartOutlineIcon } from '@chewy/kib-icons-react';
import { KibSnackbar, KibSnackbarManager } from '@chewy/kib-messaging-react';
import FetchError from 'types/http';
import MemorialPetSheet from '../pet-memorial/MemorialPetSheet';
import Error500 from '../../pages/Error500';
import AddPetLandingPage from '../add-a-pet-landing/LandingPage';
import PageLoader from '../loaders/PageLoader';
import { PetCards } from '../pet-cards';
import { MAX_PETS, getPetNewPath, getPetMemorialPath } from '../../util/config';
import './all-pets-view.scss';

export interface AllPetViewProps {
  activePets: PetInterface[] | undefined;
  inactivePets: PetInterface[] | undefined;
  loadingPets: boolean;
  analytics: Analytics;
  hasAuthError?: boolean;
  error?: FetchError;
}

const AllPetView = ({
  activePets,
  inactivePets,
  analytics,
  loadingPets,
  error,
  hasAuthError = false,
}: AllPetViewProps) => {
  const history = useHistory();
  const { petDeleted, petName } = (history.location.state as {
    petDeleted: boolean;
    petName: string;
  }) || { petDeleted: false, petName: 'Your pet' };

  const [showPetSheet, setShowPetSheet] = useState(false);

  const handleAddPetClick = () => {
    analytics.trackEvent('pet-profile-list', 'pet-profile-home', 'add-pet');
  };

  const visitMemorialLink =
    inactivePets && inactivePets.length > 1
      ? 'Visit Memorialized Profiles'
      : 'Visit Memorialized Profile';

  const handleMemorialLinkClick = () => {
    analytics.trackRainbow({
      name: 'pet profile memorial - clicked',
      category: 'pet-profile-memorial',
      action: 'click',
      label: 'pet_profile_memorial_profiles',
    });
    if (inactivePets?.length === 1) {
      window.location.href = getPetMemorialPath(inactivePets[0].id);
    } else if (inactivePets && inactivePets.length > 1) {
      setShowPetSheet(true);
    }
  };

  return (
    <div className="all-pets all-pets--center">
      <Helmet>
        <title>Pet Profiles | Chewy.com</title>
      </Helmet>
      {loadingPets || hasAuthError ? (
        <PageLoader />
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          {activePets && inactivePets ? (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              <section
                className={
                  activePets && activePets.length > 0
                    ? 'all-pets__header'
                    : 'add-a-pet-container-rainbow'
                }
                data-testid={activePets && activePets.length > 0 ? 'pets-list' : 'no-pets'}
              >
                <h1 className="kib-typography-title1 all-pets__title">My Pets</h1>
                {activePets && activePets.length > 0 ? (
                  <>
                    {activePets.length < MAX_PETS ? (
                      <KibButtonNew
                        emphasis="secondary"
                        onClick={handleAddPetClick}
                        href={getPetNewPath()}
                        className="js-add-pet-ga"
                        data-testid="add-pet-testid"
                      >
                        Add a Pet
                      </KibButtonNew>
                    ) : null}
                    <div className="pets-card-list">
                      <PetCards petProfiles={activePets} analytics={analytics} />
                    </div>
                  </>
                ) : (
                  <AddPetLandingPage analytics={analytics} />
                )}
                {!!inactivePets && inactivePets.length > 0 && (
                  <button
                    data-testid="all-pets-memorial-link"
                    type="button"
                    onClick={handleMemorialLinkClick}
                    className="all-pets__memorial"
                  >
                    <KibHeartOutlineIcon className="all-pets__memorial--icon" />
                    {visitMemorialLink}
                  </button>
                )}
              </section>

              {petDeleted && (
                <KibSnackbarManager offset={24} visible={true}>
                  <KibSnackbar className="all-pets__snackbar">
                    {petName}'s profile was removed.
                  </KibSnackbar>
                </KibSnackbarManager>
              )}
              {inactivePets && showPetSheet && (
                <MemorialPetSheet
                  pets={inactivePets}
                  showPetSheet={showPetSheet}
                  setShowPetSheet={setShowPetSheet}
                />
              )}
            </>
          ) : (
            <Error500 />
          )}
        </>
      )}
    </div>
  );
};

export default AllPetView;
